import React, { useEffect, useRef } from 'react';
import { AdAsset } from '../types';
import { ExternalLink } from 'lucide-react';

interface AdTickerProps {
    assets: AdAsset[];
    speed?: 'slow' | 'normal' | 'fast';
    variant: 'tv' | 'mobile';
    theme?: 'dark' | 'light';
    primaryColor?: string;
    hospitalId?: string;
}

const SPEED_MAP = {
    slow: { base: 40, perChar: 0.50 },
    normal: { base: 18, perChar: 0.22 },
    fast: { base: 6, perChar: 0.08 },
};

const AdTicker: React.FC<AdTickerProps> = ({
    assets,
    speed = 'normal',
    variant,
    theme = 'light',
    primaryColor = '#5B65DC',
    hospitalId,
}) => {
    const tickerAssets = assets
        .filter(a => a.isActive && a.type === 'TICKER')
        .filter(a => {
            if (a.target === 'ALL') return true;
            if (a.target === 'TV_ONLY' && variant === 'tv') return true;
            if (a.target === 'MOBILE_ONLY' && variant === 'mobile') return true;
            return false;
        })
        .sort((a, b) => a.priority - b.priority);

    const trackedRef = useRef(false);

    useEffect(() => {
        if (!hospitalId || tickerAssets.length === 0 || trackedRef.current) return;
        
        const storageKey = 'hs_ads_last_seen';
        const lastSeenObj = JSON.parse(localStorage.getItem(storageKey) || '{}');
        const now = Date.now();
        // TV = 1h, Mobile = 24h
        const COOLDOWN_MS = variant === 'mobile' ? (24 * 60 * 60 * 1000) : (1 * 60 * 60 * 1000);

        const newAds = tickerAssets.filter(ad => {
            const lastSeen = lastSeenObj[ad.id] || 0;
            return (now - lastSeen) > COOLDOWN_MS;
        });

        if (newAds.length > 0) {
            const events = newAds.map(ad => ({
                hospitalId,
                adId: ad.id,
                adType: 'TICKER',
                source: variant,
                eventType: 'impression',
                sessionHash: btoa(navigator.userAgent).slice(0, 12),
            }));

            fetch('/api/ads/track/batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ events }),
            }).catch(() => {});

            newAds.forEach(ad => {
                lastSeenObj[ad.id] = now;
            });
            localStorage.setItem(storageKey, JSON.stringify(lastSeenObj));
        }

        trackedRef.current = true;
    }, [tickerAssets, hospitalId, variant]);

    if (tickerAssets.length === 0) return null;

    const isDark = theme === 'dark';
    const isMobile = variant === 'mobile';

    const handleAdClick = (ad: AdAsset) => {
        if (!isMobile || !ad.linkUrl || !hospitalId) return;
        fetch('/api/ads/track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                hospitalId,
                adId: ad.id,
                adType: 'TICKER',
                source: 'mobile',
                eventType: 'click',
                sessionHash: btoa(navigator.userAgent).slice(0, 12),
            }),
        }).catch(() => {});
        // Ensure URL has a protocol — browsers treat "www.google.com" as a relative path
        const raw = ad.linkUrl.trim();
        const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const speedSettings = SPEED_MAP[speed] || SPEED_MAP.normal;
    // Calculate full text length as an approximation to timing
    const fullTextLength = tickerAssets.reduce((acc, a) => acc + (a.text?.length || 0) + 7, 0) * 2;
    const duration = speedSettings.base + (fullTextLength * speedSettings.perChar);

    const determineTextSize = () => {
        const size = tickerAssets[0]?.textSize || 'md';
        if (size === 'xl') return isMobile ? 'text-xl' : 'text-4xl md:text-5xl leading-none';
        if (size === 'lg') return isMobile ? 'text-lg' : 'text-3xl md:text-4xl leading-none';
        if (size === 'sm') return isMobile ? 'text-[10px]' : 'text-xl md:text-2xl leading-none';
        return isMobile ? 'text-xs' : 'text-2xl md:text-3xl leading-none';
    };

    const renderTickerItems = () => {
        return tickerAssets.map((ad, idx) => {
            const hasLink = isMobile && ad.linkUrl;
            return (
                <span key={`${ad.id}-${idx}`} className="mx-4 flex items-center pr-4 border-r border-current opacity-90 last:border-0 last:pr-0">
                    <span 
                        className={`${hasLink ? 'cursor-pointer hover:underline text-indigo-400' : ''}`}
                        onClick={hasLink ? () => handleAdClick(ad) : undefined}
                    >
                        {ad.text}
                    </span>
                    {hasLink && <ExternalLink className="w-4 h-4 ml-2 mb-0.5" />}
                </span>
            );
        });
    };

    return (
        <div
            className={`w-full overflow-hidden relative flex items-center ${isMobile ? 'py-1' : 'py-1 md:py-1.5'}`}
            style={{
                background: 'transparent',
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`,
            }}
        >
            <div
                className="ad-ticker-scroll flex items-center whitespace-nowrap"
                style={{
                    animationDuration: `${duration}s`,
                    paddingLeft: '100%',
                }}
            >
                <div
                    className={`flex items-center ${determineTextSize()} tracking-widest font-bold uppercase pb-0.5`}
                    style={{
                        color: isDark ? 'rgba(255,255,255,0.7)' : '#475569',
                    }}
                >
                    {renderTickerItems()}
                    {renderTickerItems()}
                </div>
            </div>
        </div>
    );
};

export default AdTicker;
